import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import DataTable, { DataTableEmptyState } from "common/Components/DataTable";
import StatusPill from "common/Components/StatusPill";
import {
  buildPlatformUsersResultSummary,
  getPlatformAccountStatusTone,
  getPlatformUserListRowAction,
  isPlatformUserEmailClickable,
  mapPlatformListFiltersToColumnFilters,
  PLATFORM_USER_ACCESS_ACCOUNT_STATUS_FILTER_OPTIONS,
  PLATFORM_USER_ACCESS_UI,
  DEFAULT_PLATFORM_LIST_FILTERS,
} from "../PlatformUserAccess.constants";
import {
  AccessActionButton,
  ClearFiltersButton,
  EmailActionButton,
  EmailCellText,
  FaintCellText,
  MonoCellText,
  MutedCellText,
  PrimaryCellText,
  ResultsBar,
  ResultsSummary,
  TableRowActionCell,
  UserAccessListLayout,
} from "./UserAccessList.styles";

const displayCell = (value) => {
  const text = String(value ?? "").trim();
  return text || "—";
};

const hasActivePlatformFilters = (filters) =>
  Object.keys(DEFAULT_PLATFORM_LIST_FILTERS).some(
    (key) => (filters?.[key] ?? "") !== ""
  );

const UserAccessList = ({
  rows,
  loading = false,
  pagination,
  listFilters,
  onListFiltersChange,
  onClearFilters,
  platformRoleFilterOptions,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
  onRowAction,
}) => {
  const filtersActive = hasActivePlatformFilters(listFilters);
  const shownCount = pagination.totalCount;
  const [grandTotal, setGrandTotal] = useState(0);

  useEffect(() => {
    if (!filtersActive) {
      setGrandTotal(Number(shownCount) || 0);
    }
  }, [filtersActive, shownCount]);

  const resultSummary = buildPlatformUsersResultSummary(
    shownCount,
    filtersActive,
    grandTotal
  );

  const columns = useMemo(
    () => [
      {
        id: "email",
        label: PLATFORM_USER_ACCESS_UI.tableColumns.email,
        sortable: false,
        minWidth: 162,
        filter: { type: "text" },
        render: (value, row) => {
          const label = displayCell(value);
          if (!isPlatformUserEmailClickable(row.status)) {
            return <EmailCellText>{label}</EmailCellText>;
          }
          return (
            <EmailActionButton
              type="button"
              onClick={() => onRowClick(row.id)}
            >
              {label}
            </EmailActionButton>
          );
        },
      },
      {
        id: "firstName",
        label: PLATFORM_USER_ACCESS_UI.tableColumns.firstName,
        sortable: false,
        minWidth: 96,
        filter: { type: "text" },
        render: (value) => {
          const text = String(value ?? "").trim();
          if (!text) {
            return <FaintCellText>—</FaintCellText>;
          }
          return <MutedCellText>{text}</MutedCellText>;
        },
      },
      {
        id: "lastName",
        label: PLATFORM_USER_ACCESS_UI.tableColumns.lastName,
        sortable: false,
        minWidth: 96,
        filter: { type: "text" },
        render: (value) => {
          const text = String(value ?? "").trim();
          if (!text) {
            return <FaintCellText>—</FaintCellText>;
          }
          return <MutedCellText>{text}</MutedCellText>;
        },
      },
      {
        id: "phone",
        label: PLATFORM_USER_ACCESS_UI.tableColumns.phone,
        sortable: false,
        minWidth: 120,
        filter: { type: "text" },
        render: (value) => {
          const text = String(value ?? "").trim();
          if (!text) {
            return <FaintCellText>—</FaintCellText>;
          }
          return <MonoCellText>{text}</MonoCellText>;
        },
      },
      {
        id: "platformRole",
        label: PLATFORM_USER_ACCESS_UI.tableColumns.platformRole,
        sortable: false,
        minWidth: 120,
        filter: {
          type: "select",
          options: platformRoleFilterOptions ?? [],
        },
        render: (value) => {
          const text = displayCell(value);
          const isNone =
            text === "—" ||
            text === PLATFORM_USER_ACCESS_UI.defaultPlatformRole;
          if (isNone) {
            return <FaintCellText>{text}</FaintCellText>;
          }
          return <PrimaryCellText>{text}</PrimaryCellText>;
        },
      },
      {
        id: "status",
        label: PLATFORM_USER_ACCESS_UI.tableColumns.accountStatus,
        sortable: false,
        minWidth: 128,
        filter: {
          type: "select",
          options: PLATFORM_USER_ACCESS_ACCOUNT_STATUS_FILTER_OPTIONS,
        },
        render: (value) => {
          const label = normalizeStatusLabel(value);
          if (!label) {
            return <FaintCellText>—</FaintCellText>;
          }
          return (
            <StatusPill
              label={label}
              tone={getPlatformAccountStatusTone(value)}
            />
          );
        },
      },
      {
        id: "action",
        label: PLATFORM_USER_ACCESS_UI.tableColumns.action,
        sortable: false,
        minWidth: 106,
        render: (_, row) => {
          const rowAction = getPlatformUserListRowAction(row.status);
          if (!rowAction || typeof onRowAction !== "function") {
            return null;
          }
          return (
            <TableRowActionCell onClick={(event) => event.stopPropagation()}>
              <AccessActionButton
                type="button"
                onClick={() => onRowAction(row, rowAction.action)}
                aria-label={`${rowAction.label} ${row.email || ""}`.trim()}
              >
                {rowAction.label}
              </AccessActionButton>
            </TableRowActionCell>
          );
        },
      },
    ],
    [onRowAction, onRowClick, platformRoleFilterOptions]
  );

  const tableFilterValues = useMemo(
    () => mapPlatformListFiltersToColumnFilters(listFilters),
    [listFilters]
  );

  const emptyState = (
    <DataTableEmptyState
      title={PLATFORM_USER_ACCESS_UI.emptyStateTitle}
      hint={PLATFORM_USER_ACCESS_UI.emptyStateHint}
    />
  );

  return (
    <UserAccessListLayout>
      <ResultsBar>
        <ResultsSummary aria-live="polite" component="p">
          {resultSummary}
        </ResultsSummary>
        <ClearFiltersButton
          type="button"
          $active={filtersActive}
          disabled={!filtersActive}
          onClick={onClearFilters}
        >
          {PLATFORM_USER_ACCESS_UI.clearFiltersLabel}
        </ClearFiltersButton>
      </ResultsBar>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        getRowId={(row) => row.id}
        showExport={false}
        hideToolbar
        serverMode
        page={pagination.page}
        totalRows={pagination.totalCount}
        defaultRowsPerPage={pagination.size}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        onFilterChange={onListFiltersChange}
        filterValues={tableFilterValues}
        applyTextFiltersOnCommit
        renderEmptyState={() => emptyState}
      />
    </UserAccessListLayout>
  );
};

const normalizeStatusLabel = (status) => {
  const text = String(status ?? "").trim();
  return text || "";
};

UserAccessList.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  pagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
    size: PropTypes.number.isRequired,
  }).isRequired,
  listFilters: PropTypes.object,
  onListFiltersChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  platformRoleFilterOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
  onRowAction: PropTypes.func.isRequired,
};

UserAccessList.defaultProps = {
  loading: false,
  platformRoleFilterOptions: [],
};

export default UserAccessList;
